import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import instance from "../../apiServices/instance";
import RiseFormationsLogo from "../../assets/logo/riseformations-logo.webp";

const EYEBROW_LABELS = [
  "BEST PRICE GUARANTEE",
  "KEEP HOME ADDRESS HIDDEN",
  "AMBITIOUS ENTREPRENEURS",
];

const Package = ({ handleClickSetSix }) => {
  const navigate = useNavigate();
  const [packageData, setPackageData] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState([]);
  const [companyHouseFee, setCompanyHouseFee] = useState("");
  const [addressService, setAddressService] = useState("");

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const buildSelectedPackage = (item) => ({
    ...item,
    companyHousePriceId: companyHouseFee[0]?.priceId,
    AddressServicePriceId: item.features.includes(
      "Central London Registered Address"
    )
      ? addressService[0]?.priceId
      : "",
  });

  const handleCardClick = (item) => {
    setSelectedPackage(buildSelectedPackage(item));
  };

  const handleSelectPackage = (event, item) => {
    event.stopPropagation();
    const selected = buildSelectedPackage(item);
    setSelectedPackage(selected);
    handleClickSetSix(selected);
  };
  const FetchPackageList = async () => {
    const { data } = await instance.get(`/api/stripe/products/fetch`);
    const companyHouseData = [];
    const packageData = [];
    const AddressServiceData = [];

    data.forEach(({ id, name, type, prices, coupon, features = [] }) => {
      const priceInPounds = prices?.[0]?.amount
        ? (prices[0].amount / 100).toFixed(2).replace(/\.00$/, "")
        : "0";

      const item = {
        id,
        packageName: name,
        price: priceInPounds,
        priceId: prices?.[0]?.id,
        // additionalFee: "+ £89 + VAT minimum Companies House fee",
        features,
        coupon,
      };

      if (type === "COMPANY_HOUSE_FEE") {
        companyHouseData.push(item);
      } else if (type === "ADDRESS_SERVICE") {
        AddressServiceData.push(item);
      } else {
        packageData.push(item);
      }
    });
    // Sort packageData by number of features (ascending)
    packageData.sort((a, b) => a.features.length - b.features.length);
    return { packageData, companyHouseData, AddressServiceData };
  };

  useEffect(() => {
    const fetchData = async () => {
      const { packageData, companyHouseData, AddressServiceData } =
        await FetchPackageList();
      setPackageData(packageData);
      const defaultPackage = packageData[1] || packageData[0];
      setSelectedPackage({
        ...defaultPackage,
        companyHousePriceId: companyHouseData?.[0]?.priceId || "",
        AddressServicePriceId: defaultPackage?.features?.includes(
          "Central London Registered Address"
        )
          ? AddressServiceData?.[0]?.priceId || ""
          : "",
      });
      setCompanyHouseFee(companyHouseData);
      setAddressService(AddressServiceData);
    };

    fetchData();
  }, []);

  const companyHousePrice = companyHouseFee[0]?.price;
  const baseFeatureSet = new Set(packageData[0]?.features || []);

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      justifyContent={"center"}
      alignItems="center"
      sx={{ paddingTop: "0.5rem" }}
    >
      <Box
        sx={{
          width: "100%",
          padding: isSmallScreen ? "0.75rem 1rem" : "1rem 2.5rem",
        }}
      >
        <img
          src={RiseFormationsLogo}
          alt="Rise Formations"
          style={{ height: "1.75rem" }}
        />
      </Box>
      <Typography
        variant="h3"
        sx={{
          fontWeight: "700",
          fontSize: "1.875rem",
          color: "#0F0049",
          textAlign: "center",
          padding: isSmallScreen ? "0 1rem" : "0 2rem",
        }}
      >
        Choose your company formation package
      </Typography>
      {companyHousePrice && (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.875rem",
            color: "#82808A",
            marginTop: "0.5rem",
            marginBottom: "2.5rem",
            textAlign: "center",
          }}
        >
          All packages include minimum Companies House fee{" "}
          <Box
            component="span"
            sx={{ textDecoration: "line-through", fontWeight: 900, opacity: 0.6 }}
          >
            £100
          </Box>{" "}
          <Box component="span" sx={{ fontWeight: 600, color: "#0F0049" }}>
            £{companyHousePrice}
          </Box>{" "}
          on top
        </Typography>
      )}
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="center"
        width="100%"
        gap={2}
        sx={{
          padding: isSmallScreen ? "0 1rem" : "0 2rem",
        }}
      >
        {packageData.length > 0 ? (
          packageData.map((item, index) => {
            const isRecommended = index === 1;
            const isSelected = selectedPackage.packageName === item.packageName;
            return (
              <Box
                key={index}
                display="flex"
                flexDirection="column"
                width={isSmallScreen ? "100%" : "30%"}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    color: "#82808A",
                    textAlign: "center",
                    marginBottom: "0.75rem",
                  }}
                >
                  {EYEBROW_LABELS[index] || ""}
                </Typography>
                <Box
                  onClick={() => handleCardClick(item)}
                  backgroundColor="white"
                  sx={{
                    position: "relative", // Important for placing the "Recommended" badge
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    border: isSelected
                      ? "2px solid #1f69f7"
                      : "1px solid #E0DFE5",
                    borderRadius: "1rem",
                    padding: "1rem",
                    boxShadow: isSelected
                      ? "0 4px 8px rgba(0,0,0,0.1)"
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  {isRecommended && (
                    <Typography
                      sx={{
                        position: "absolute",
                        top: "-10px", // Adjust to place above the card
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#1f69f7",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        borderRadius: "0.5rem",
                        padding: "0.25rem 1rem",
                        textAlign: "center",
                      }}
                    >
                      Recommended
                    </Typography>
                  )}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1.3rem",
                      mt: "0.5rem",
                    }}
                  >
                    {item.packageName}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, fontSize: "2rem", color: "#1f69f7" }}
                  >
                    £{item.price}
                  </Typography>
                  {companyHousePrice && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.9rem",
                        marginBottom: "0.7rem",
                        fontWeight: 600,
                      }}
                    >
                      +{" "}
                      <Box component="span" sx={{ textDecoration: "line-through" }}>
                        £100
                      </Box>{" "}
                      £{companyHousePrice} Companies House Fee
                    </Typography>
                  )}
                  <Box sx={{ borderBottom: "1px solid #E0DFE5", marginBottom: "0.7rem" }} />
                  <Box sx={{ flex: 1 }}>
                    {[...item.features]
                      .sort((a, b) => {
                        const aIsExtra = baseFeatureSet.has(a) ? 0 : 1;
                        const bIsExtra = baseFeatureSet.has(b) ? 0 : 1;
                        return aIsExtra - bIsExtra;
                      })
                      .map((feature, featureIndex) => {
                      const isPremium = !baseFeatureSet.has(feature);
                      return (
                        <Box
                          key={featureIndex}
                          display="flex"
                          alignItems="flex-start"
                          gap={0.5}
                          sx={{ marginBottom: "0.4rem" }}
                        >
                          <CheckIcon
                            sx={{ fontSize: "1.1rem", color: "#1f69f7", mt: "0.1rem" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.9rem",
                              color: isPremium ? "#1f69f7" : "inherit",
                              fontWeight: isPremium ? 600 : 400,
                            }}
                          >
                            {feature}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  <Button
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={(event) => handleSelectPackage(event, item)}
                    sx={{
                      marginTop: "1rem",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "1rem",
                      borderRadius: "0.75rem",
                      padding: "0.5rem 1rem",
                      ...(isSelected
                        ? {
                            backgroundColor: "#1f69f7",
                            color: "#ffffff",
                            "&:hover": { backgroundColor: "#1859c6" },
                          }
                        : {
                            borderColor: "#E0DFE5",
                            color: "#0F0049",
                            "&:hover": { borderColor: "#1f69f7" },
                          }),
                    }}
                  >
                    Select
                  </Button>
                </Box>
              </Box>
            );
          })
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "20vh", // Adjust height as needed
            }}
          >
            <CircularProgress
              thickness={3.6}
              size={40}
              sx={{ color: "primary" }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Package;
